import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../lib/apiBase';

/**
 * AuthContext — admin authentication.
 *
 *   - `user`             null when signed out, {username, name, is_admin} when in
 *   - `loading`          true on first mount while we poll /auth/me
 *   - `setUser(user)`    set after a successful POST /auth/login (called by
 *                        the AdminLogin form)
 *   - `logout()`         POSTs /auth/logout and clears local state
 *   - `checkAuth()`      force re-fetch /auth/me
 *
 * Session lives in a backend-issued httpOnly `session_token` cookie.
 * Requests include `withCredentials:true` so the cookie flows on every
 * /api/* call.
 */

const AuthContext = createContext();
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/auth/me`);
      setUser(data);
      return data;
    } catch (_) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE}/api/auth/logout`);
    } catch (_) { /* ignore */ }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, setUser, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
